/**
 * Mobile sticky product bar — Figma "COLOR REDESIGN SEPT 2026" (node 2339-4480).
 *
 * The bar is a fixed copy of the compact header rendered in flow below the gallery (same markup, same geometry:
 * padding 32px / container gutter / 12px). It is shown whenever that header is not fully in view (under the fold
 * on load, or scrolled past) and the footer is not in view.
 *
 * The hand-off is done with display:none (no fade) and evaluated synchronously on every scroll frame, so the bar
 * disappears in the exact frame where its fixed position coincides with the in-flow header — the header simply
 * "takes over" in place. IntersectionObserver is not used here: its callbacks are asynchronous and would leave a
 * one-frame double or gap.
 */
if (!customElements.get('product-sticky-mobile')) {
  class ProductStickyMobile extends HTMLElement {
    static ATTRIBUTES = { startMarker: 'start-marker' };
    static SELECTORS = { footer: '.shopify-section--footer' };
    static CLASSES = { visible: 'is-visible' };
    static EVENTS = { scroll: 'scroll', resize: 'resize' };

    // Sub-pixel rounding of getBoundingClientRect must not keep the bar one frame too long.
    static EDGE_TOLERANCE_PX = 0.5;

    connectedCallback() {
      this._startMarker = document.getElementById(this.getAttribute(ProductStickyMobile.ATTRIBUTES.startMarker));
      this._footer = document.querySelector(ProductStickyMobile.SELECTORS.footer);

      if (!this._startMarker) return;

      this._abort = new AbortController();
      const { signal } = this._abort;
      const update = () => this._updateVisibility();

      window.addEventListener(ProductStickyMobile.EVENTS.scroll, update, { passive: true, signal });
      window.addEventListener(ProductStickyMobile.EVENTS.resize, update, { signal });

      // Layout shifts without scrolling (images loading, lazy sections) move the in-flow header as well
      this._resizeObserver = new ResizeObserver(update);
      this._resizeObserver.observe(document.documentElement);

      update();
    }

    disconnectedCallback() {
      this._abort?.abort();
      this._resizeObserver?.disconnect();
    }

    _isStartMarkerFullyInView() {
      const { top, bottom } = this._startMarker.getBoundingClientRect();
      const tolerance = ProductStickyMobile.EDGE_TOLERANCE_PX;

      return top >= -tolerance && bottom <= window.innerHeight + tolerance;
    }

    _isFooterInView() {
      if (!this._footer) return false;

      return this._footer.getBoundingClientRect().top < window.innerHeight;
    }

    _updateVisibility() {
      const visible = !this._isStartMarkerFullyInView() && !this._isFooterInView();
      this.classList.toggle(ProductStickyMobile.CLASSES.visible, visible);
    }
  }

  customElements.define('product-sticky-mobile', ProductStickyMobile);
}
